import React, { useState, useMemo, useEffect } from "react";
import { Globe, ChevronDown, ChevronUp, Loader2, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { invokeSerperPlayground } from "@/race-tracker/api/rtClient";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/race-tracker/components/ui/tabs";
import { Button } from "@/race-tracker/components/ui/button";
import { Input } from "@/race-tracker/components/ui/input";
import { Label } from "@/race-tracker/components/ui/label";
import { Textarea } from "@/race-tracker/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/race-tracker/components/ui/select";
import { Badge } from "@/race-tracker/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/race-tracker/components/ui/collapsible";
import SerperQuerySetupTab from "./SerperQuerySetupTab";
import SerperOrganicHitList from "./SerperOrganicHitList";
import { SerperColumnFieldGrid } from "./SerperColumnFieldGrid";
import {
  SERPER_DEFAULT_GL,
  SERPER_DEFAULT_HL,
  buildSerperQueryPreview,
  normalizeSerperQueryFields,
  normalizeSerperOrganicItem,
  normalizeSerperNewsItem,
  normalizeSerperMode,
} from "@/race-tracker/lib/serperQueryCatalog";

function SerperPlaygroundBlock({
  race,
  advertiserName,
  settingsHref,
  sweepConfig,
}) {
  const [playgroundFields, setPlaygroundFields] = useState(() =>
    normalizeSerperQueryFields(race?.serper_query_fields)
  );
  const [playgroundMode, setPlaygroundMode] = useState(() => normalizeSerperMode(race?.serper_mode));
  const [q, setQ] = useState("");
  const [qTouched, setQTouched] = useState(false);
  const [gl, setGl] = useState(SERPER_DEFAULT_GL);
  const [hl, setHl] = useState(SERPER_DEFAULT_HL);
  const [tbs, setTbs] = useState("");
  const [page, setPage] = useState("1");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [durationMs, setDurationMs] = useState(null);
  const [credits, setCredits] = useState(null);
  const [jsonText, setJsonText] = useState("");
  const [organicHits, setOrganicHits] = useState([]);
  const [rawOpen, setRawOpen] = useState(false);

  useEffect(() => {
    setPlaygroundFields(normalizeSerperQueryFields(race?.serper_query_fields));
    setPlaygroundMode(normalizeSerperMode(race?.serper_mode));
  }, [race?.id, race?.serper_query_fields, race?.serper_mode]);

  const columnPreview = useMemo(
    () => buildSerperQueryPreview({ ...race, serper_query_fields: playgroundFields }, advertiserName),
    [race, playgroundFields, advertiserName]
  );

  useEffect(() => {
    if (sweepConfig?.serper_tbs != null) setTbs(String(sweepConfig.serper_tbs));
  }, [sweepConfig?.serper_tbs]);

  useEffect(() => {
    if (!qTouched) setQ(columnPreview);
  }, [columnPreview, qTouched]);

  const applyColumnsToQuery = () => {
    setQTouched(false);
    setQ(columnPreview);
  };

  const run = async () => {
    setError("");
    setJsonText("");
    setOrganicHits([]);
    setCredits(null);
    setDurationMs(null);
    const p = parseInt(page, 10);
    const pageNum = Number.isFinite(p) ? p : 1;
    const t0 = performance.now();
    setLoading(true);
    try {
      const out = await invokeSerperPlayground({
        q: q.trim(),
        mode: playgroundMode,
        gl: gl.trim() || undefined,
        hl: hl.trim() || undefined,
        tbs: tbs.trim() || undefined,
        page: pageNum,
      });
      setDurationMs(Math.round(performance.now() - t0));
      if (out.ok && out.data !== undefined) {
        setJsonText(JSON.stringify(out.data, null, 2));
        const data = out.data;
        const organic = Array.isArray(data?.organic) ? data.organic : [];
        const news = Array.isArray(data?.news) ? data.news : [];
        if (playgroundMode === "news") {
          setOrganicHits(news.map((item, i) => normalizeSerperNewsItem(item, i)));
        } else {
          setOrganicHits(organic.map((item, i) => normalizeSerperOrganicItem(item, i)));
        }
        if (typeof out.credits === "number") setCredits(out.credits);
      } else {
        setError(out.message || "Request failed");
        if (out.provider_body_preview) setJsonText(String(out.provider_body_preview));
        else if (out.data !== undefined) setJsonText(JSON.stringify(out.data, null, 2));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-4 py-4 space-y-4">
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        Live Serper requests use the API key from{" "}
        <Link to={settingsHref} className="text-primary underline hover:no-underline">
          Integrations
        </Link>
        . Defaults: <span className="font-mono">gl=us</span>, <span className="font-mono">hl=en</span>. Build{" "}
        <span className="font-mono">q</span> from race columns or edit manually. News mode sends only{" "}
        <span className="font-mono">q</span> and <span className="font-mono">page</span> to Serper; locale fields apply to web search.
      </p>

      <div className="space-y-1.5 max-w-xs">
        <Label className="text-xs">Serper mode</Label>
        <Select value={playgroundMode} onValueChange={(v) => setPlaygroundMode(normalizeSerperMode(v))}>
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="news" className="text-xs">
              News
            </SelectItem>
            <SelectItem value="search" className="text-xs">
              Web search
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-lg border border-border bg-muted/15 p-3 space-y-2">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Columns → query</p>
        <SerperColumnFieldGrid fields={playgroundFields} onFieldsChange={setPlaygroundFields} />
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="secondary" size="sm" className="text-xs h-8" onClick={applyColumnsToQuery}>
            Apply columns to query (q)
          </Button>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Query (q)</Label>
        <Textarea
          value={q}
          onChange={(e) => {
            setQTouched(true);
            setQ(e.target.value);
          }}
          rows={3}
          className="text-sm font-mono"
          placeholder="Search query…"
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Country (gl)</Label>
          <Input
            value={gl}
            onChange={(e) => setGl(e.target.value)}
            className="text-xs h-8"
            placeholder="us"
            disabled={playgroundMode === "news"}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Language (hl)</Label>
          <Input
            value={hl}
            onChange={(e) => setHl(e.target.value)}
            className="text-xs h-8"
            placeholder="en"
            disabled={playgroundMode === "news"}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Date range (tbs)</Label>
          <Input
            value={tbs}
            onChange={(e) => setTbs(e.target.value)}
            className="text-xs h-8"
            placeholder="optional"
            disabled={playgroundMode === "news"}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Page</Label>
          <Input value={page} onChange={(e) => setPage(e.target.value)} className="text-xs h-8" placeholder="1" inputMode="numeric" />
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Button type="button" size="sm" className="text-xs h-8 gap-1.5" onClick={run} disabled={loading || !q.trim()}>
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          {loading ? "Running…" : playgroundMode === "news" ? "Run news" : "Run web search"}
        </Button>
        {durationMs != null && <span className="text-[11px] text-muted-foreground tabular-nums">Duration: {durationMs}ms</span>}
        {credits != null && <span className="text-[11px] text-muted-foreground tabular-nums">Credits: {credits}</span>}
      </div>

      {error && <p className="text-xs text-destructive">{error}</p>}

      {organicHits.length > 0 && (
        <div className="space-y-2">
          <Label className="text-xs font-medium">Results</Label>
          <SerperOrganicHitList hits={organicHits} />
        </div>
      )}

      {jsonText ? (
        <Collapsible open={rawOpen} onOpenChange={setRawOpen} className="space-y-1">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground w-full text-left py-1"
            >
              <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform ${rawOpen ? "rotate-90" : ""}`} />
              Raw JSON (advanced)
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <pre className="text-[10px] leading-relaxed font-mono bg-muted/50 border border-border rounded-md p-3 max-h-[min(320px,40vh)] overflow-auto whitespace-pre-wrap break-words">
              {jsonText}
            </pre>
          </CollapsibleContent>
        </Collapsible>
      ) : null}
    </div>
  );
}

/** Serper sweep results, query setup, and playground (Intel Sweep). */
export default function GoogleSection({
  serperResults = [],
  isSweeping,
  serperSkipped,
  settingsHref = "/settings",
  race = null,
  raceLabel = "",
  advertiserName = "",
  opponents = [],
  sweepConfig = null,
  sweepConfigLoading = false,
  isSuperAdmin = false,
  onSaveSerperRaceQuery,
  onSaveSerperLocale,
}) {
  const [open, setOpen] = useState(true);

  const serperSweepKindLabel = useMemo(() => {
    const ep = serperResults.find((r) => r && typeof r === "object" && r.serper_endpoint)?.serper_endpoint;
    if (ep === "news") return "News";
    if (ep === "search") return "Web";
    return normalizeSerperMode(race?.serper_mode) === "search" ? "Web" : "News";
  }, [serperResults, race?.serper_mode]);

  const serperSkip = typeof serperSkipped === "string" ? serperSkipped : "";
  const { serperNotConfigured, serperApiError } = useMemo(() => {
    const s = serperSkip;
    const serperNotConfigured = !!s && /not configured|no api key/i.test(s);
    const serperApiError = !!s && /serper/i.test(s) && !serperNotConfigured;
    return { serperNotConfigured, serperApiError };
  }, [serperSkip]);

  const sweepResultsBody = (
    <div>
      {/* Serper sweep hits */}
      <div>
        <div className="px-4 py-2 bg-muted/30 border-b border-border flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground shrink-0">Serper</p>
            <Badge variant="secondary" className="text-[9px] font-medium px-1.5 py-0 h-5 shrink-0">
              {serperSweepKindLabel}
            </Badge>
          </div>
          {!isSweeping && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground font-medium tabular-nums">
              {serperResults.length} hit{serperResults.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>
        {serperNotConfigured ? (
          <div className="px-4 py-4 space-y-2 text-sm text-muted-foreground">
            <p>
              Serper is not configured. Add a Serper API key under{" "}
              <Link to={settingsHref} className="text-primary underline hover:no-underline">
                Integrations
              </Link>{" "}
              to run automatic web search on each sweep.
            </p>
          </div>
        ) : serperApiError && serperResults.length === 0 ? (
          <div className="px-4 py-4 space-y-2 text-sm text-muted-foreground">
            <p className="line-clamp-4 text-amber-700 dark:text-amber-500/90">{serperSkip}</p>
            <p className="text-xs">
              Fix Serper settings or your network, then re-run the sweep. Other sweep sources are unchanged.
            </p>
          </div>
        ) : serperSkip && /empty query/i.test(serperSkip) ? (
          <div className="px-4 py-4 text-sm text-muted-foreground space-y-2">
            <p className="text-amber-700 dark:text-amber-500/90">{serperSkip}</p>
            <p className="text-xs">
              Open the <strong>Serper Query Setup</strong> tab, select columns with values, save, then re-run the sweep.
            </p>
          </div>
        ) : serperResults.length === 0 ? (
          <div className="px-4 py-4 text-sm text-muted-foreground">
            <p>No Serper results for the composed query.</p>
            {serperSkip && !serperNotConfigured && (
              <p className="text-xs mt-2 text-amber-700 dark:text-amber-500/90">{serperSkip}</p>
            )}
          </div>
        ) : (
          serperResults.map((r, i) => (
            <div key={`${r.link || "s"}-${i}`} className="px-4 py-3 space-y-1.5 border-b border-border/60 last:border-b-0">
              <div className="flex items-start gap-2">
                {r.position != null && (
                  <span className="shrink-0 text-[10px] font-mono tabular-nums px-1.5 py-0.5 rounded bg-muted text-muted-foreground mt-0.5">
                    #{r.position}
                  </span>
                )}
                <a
                  href={r.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-primary hover:underline flex-1 leading-snug"
                >
                  {r.title || "Untitled"}
                </a>
              </div>
              <div className="text-[10px] text-muted-foreground pl-0 sm:pl-8">{r.displayLink || "—"}</div>
              {(r.publisher || r.news_date) && (
                <div className="text-[10px] text-muted-foreground/90 pl-0 sm:pl-8 flex flex-wrap gap-x-2 gap-y-0.5">
                  {r.publisher ? <span>{r.publisher}</span> : null}
                  {r.news_date ? (
                    <span className="tabular-nums">{r.publisher ? `· ${r.news_date}` : r.news_date}</span>
                  ) : null}
                </div>
              )}
              {r.snippet && (
                <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3 pl-0 sm:pl-8">{r.snippet}</p>
              )}
            </div>
          ))
        )}
        <p className="text-[10px] text-muted-foreground/80 px-4 py-2 border-t border-border bg-muted/10">
          Powered by{" "}
          <a href="https://serper.dev" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:no-underline">
            Serper
          </a>
          . Mode, columns, and locale: <strong>Serper Query Setup</strong> tab.
        </p>
      </div>
    </div>
  );

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        className="w-full flex items-center gap-2 px-4 py-3 bg-muted/40 hover:bg-muted/60 transition-colors text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <Globe className="w-4 h-4 text-muted-foreground shrink-0" />
        <span className="text-sm font-semibold flex-1">Web search</span>
        {isSweeping ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
        ) : (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground font-medium">
            {serperResults.length} Serper hit{serperResults.length !== 1 ? "s" : ""}
          </span>
        )}
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>

      {open && (
        <Tabs defaultValue="sweep" className="px-0 pb-0">
          <TabsList className="w-full justify-start rounded-none border-b border-border bg-muted/20 px-2 h-auto min-h-9 flex-wrap gap-0.5 py-1">
            <TabsTrigger value="sweep" className="text-xs">
              Sweep results
            </TabsTrigger>
            <TabsTrigger value="setup" className="text-xs">
              Serper Query Setup
            </TabsTrigger>
            <TabsTrigger value="serper" className="text-xs">
              Serper playground
            </TabsTrigger>
          </TabsList>
          <TabsContent value="sweep" className="mt-0">
            {sweepResultsBody}
          </TabsContent>
          <TabsContent value="setup" className="mt-0">
            {race ? (
              <SerperQuerySetupTab
                race={race}
                advertiserName={advertiserName}
                settingsHref={settingsHref}
                sweepConfig={sweepConfig}
                sweepConfigLoading={sweepConfigLoading}
                isSuperAdmin={isSuperAdmin}
                onSaveSerperRaceQuery={onSaveSerperRaceQuery}
                onSaveSerperLocale={onSaveSerperLocale}
              />
            ) : (
              <p className="text-xs text-muted-foreground px-4 py-6">Select a race to configure Serper.</p>
            )}
          </TabsContent>
          <TabsContent value="serper" className="mt-0">
            <SerperPlaygroundBlock
              race={race}
              advertiserName={advertiserName}
              settingsHref={settingsHref}
              sweepConfig={sweepConfig}
            />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
